import { z } from 'zod';
import { isoDateTime, uuid } from './common.js';
import { deliveryOption } from './shopfront.js';
// --- discounts (P2.4) ------------------------------------------------------------------------------
export const DISCOUNT_KINDS = ['percent', 'amount', 'free_shipping'];
export const DISCOUNT_SCOPES = ['order', 'products', 'categories', 'vendors', 'customers', 'groups'];
export const DISCOUNT_KIND_LABELS = { percent: 'Procent', amount: 'Beløb', free_shipping: 'Gratis fragt' };
export const DISCOUNT_SCOPE_LABELS = { order: 'Hele ordren', products: 'Produkter', categories: 'Kategorier', vendors: 'Leverandører', customers: 'Kunder', groups: 'Kundegrupper' };
export const discountDto = z.object({
    id: uuid,
    shopId: uuid.nullable(),
    shopName: z.string().nullable(),
    name: z.string(),
    code: z.string().nullable(),
    kind: z.enum(DISCOUNT_KINDS),
    percent: z.number().int().nullable(),
    amountMinor: z.number().int().nullable(),
    scope: z.enum(DISCOUNT_SCOPES),
    scopeIds: z.array(uuid),
    minOrderMinor: z.number().int(),
    startsAt: isoDateTime.nullable(),
    endsAt: isoDateTime.nullable(),
    usageLimit: z.number().int().nullable(),
    perCustomerLimit: z.number().int().nullable(),
    usedCount: z.number().int(),
    active: z.boolean(),
});
export const saveDiscountRequest = z
    .object({
    shopId: uuid.nullable(),
    name: z.string().trim().min(1).max(200),
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_-]{3,40}$/, 'A-Z, 0-9, - og _').nullable().optional().or(z.literal('').transform(() => null)),
    kind: z.enum(DISCOUNT_KINDS),
    percent: z.number().int().min(1).max(100).nullable().optional(),
    /** Excl. VAT. */
    amountMinor: z.number().int().min(1).nullable().optional(),
    scope: z.enum(DISCOUNT_SCOPES).default('order'),
    scopeIds: z.array(uuid).max(1000).default([]),
    minOrderMinor: z.number().int().min(0).default(0),
    startsAt: isoDateTime.nullable().optional(),
    endsAt: isoDateTime.nullable().optional(),
    usageLimit: z.number().int().min(1).nullable().optional(),
    perCustomerLimit: z.number().int().min(1).nullable().optional(),
    active: z.boolean().default(true),
})
    .refine((d) => d.kind !== 'percent' || !!d.percent, { message: 'Angiv procent', path: ['percent'] })
    .refine((d) => d.kind !== 'amount' || !!d.amountMinor, { message: 'Angiv beløb', path: ['amountMinor'] });
export const discountQuery = z.object({ shopId: uuid.optional() });
// --- cart and quote --------------------------------------------------------------------------------
export const sfCartLineRequest = z.object({ variantId: uuid, quantity: z.number().int().min(0).max(100000), mode: z.enum(['add', 'set']).default('add') });
export const sfCouponRequest = z.object({ code: z.string().trim().max(40).nullable() });
export const sfQuoteLineDto = z.object({
    variantId: uuid,
    productSlug: z.string(),
    sku: z.string(),
    title: z.string(),
    variantTitle: z.string(),
    image: z.string().nullable(),
    quantity: z.number().int(),
    /** As shown in the shop (incl. or excl. VAT per the shop). */
    unitMinor: z.number().int(),
    lineMinor: z.number().int(),
    available: z.number().int(),
    minQty: z.number().int(),
    maxQty: z.number().int().nullable(),
    stepQty: z.number().int(),
    allowedQty: z.array(z.number().int()),
    error: z.string().nullable(),
});
export const sfShippingOptionDto = z.object({ method: deliveryOption, priceMinor: z.number().int() });
export const sfQuoteDto = z.object({
    lines: z.array(sfQuoteLineDto),
    count: z.number().int(),
    subtotalMinor: z.number().int(),
    discountMinor: z.number().int(),
    shippingMinor: z.number().int(),
    vatMinor: z.number().int(),
    totalMinor: z.number().int(),
    pricesInclVat: z.boolean(),
    currency: z.string(),
    couponCode: z.string().nullable(),
    couponError: z.string().nullable(),
    discounts: z.array(z.object({ name: z.string(), code: z.string().nullable(), amountMinor: z.number().int() })),
    freeShipping: z.boolean(),
    /** Order value (as shown) from which home delivery is free in the shop's country; null when it never is. */
    freeShippingFromMinor: z.number().int().nullable(),
    shippingOptions: z.array(sfShippingOptionDto),
    /** Problems that block checkout (closed shop, quantities, stock, cart limit). */
    errors: z.array(z.string()),
});
const opt = (n) => z.string().trim().max(n).optional().nullable();
export const sfAddressInput = z.object({
    name: z.string().trim().min(1).max(200),
    company: opt(200),
    street1: z.string().trim().min(1).max(200),
    street2: opt(200),
    zip: z.string().trim().min(1).max(20),
    city: z.string().trim().min(1).max(100),
    countryCode: z.string().trim().length(2).default('DK'),
    phone: opt(50),
    email: z.string().trim().email().max(200).optional().nullable(),
});
export const sfCheckoutRequest = z.object({
    email: z.string().trim().toLowerCase().email().max(200),
    phone: opt(50),
    billing: sfAddressInput,
    delivery: z.object({
        method: deliveryOption,
        /** For home and business delivery when it differs from billing. */
        address: sfAddressInput.optional().nullable(),
        parcelShopId: opt(20),
        deliveryAddressId: uuid.optional().nullable(),
    }),
    paymentMethod: z.enum(['bambora', 'invoice']),
    fields: z.record(z.string(), z.string().max(500)).default({}),
    customFields: z.record(z.string(), z.string().max(500)).default({}),
    note: opt(2000),
    acceptTerms: z.boolean().default(false),
});
export const sfCheckoutResultDto = z.object({
    orderNumber: z.number().int(),
    token: z.string(),
    status: z.string(),
    /** Card payments: send the visitor to Bambora's payment window. */
    paymentUrl: z.string().nullable(),
});
// --- receipts and the gift journey ------------------------------------------------------------------
export const sfReceiptDto = z.object({
    orderNumber: z.number().int(),
    token: z.string(),
    status: z.string(),
    type: z.string(),
    placedAt: isoDateTime.nullable(),
    currency: z.string(),
    pricesInclVat: z.boolean(),
    paymentMethod: z.string(),
    paid: z.boolean(),
    lines: z.array(z.object({ sku: z.string(), title: z.string(), variantTitle: z.string().nullable(), quantity: z.number().int(), unitMinor: z.number().int(), lineMinor: z.number().int(), image: z.string().nullable() })),
    subtotalMinor: z.number().int(),
    discountMinor: z.number().int(),
    shippingMinor: z.number().int(),
    totalMinor: z.number().int(),
    delivery: z.object({ method: z.string(), parcelShopId: z.string().nullable(), address: z.array(z.string()) }),
    confirmationText: z.string(),
    /** SVG of the QR code for hand-out (gift orders). */
    qrSvg: z.string().nullable(),
});
export const sfGiftChoiceRequest = z.object({
    variantId: uuid,
    delivery: z.object({ method: deliveryOption, address: sfAddressInput.optional().nullable(), parcelShopId: opt(20), deliveryAddressId: uuid.optional().nullable() }),
    department: opt(100),
    phone: opt(50),
    customFields: z.record(z.string(), z.string().max(500)).default({}),
});
export const sfGiftStateDto = z.object({
    /** The current choice, if any. */
    receipt: sfReceiptDto.nullable(),
    declined: z.boolean(),
    /** Choosing or changing is possible while the shop is open and the order not yet released. */
    canChange: z.boolean(),
});
export const sfOrderListItemDto = z.object({ orderNumber: z.number().int(), token: z.string(), status: z.string(), placedAt: isoDateTime.nullable(), totalMinor: z.number().int(), currency: z.string(), itemCount: z.number().int() });
export const sfApprovalDto = z.object({ shopName: z.string(), orderNumber: z.number().int(), status: z.string(), customer: z.string(), totalMinor: z.number().int(), currency: z.string(), lines: z.array(z.object({ title: z.string(), quantity: z.number().int(), lineMinor: z.number().int() })), note: z.string().nullable() });
export const sfApprovalRequest = z.object({ token: z.string().min(20).max(200), decision: z.enum(['approve', 'reject']), comment: z.string().max(500).optional() });
export const sfDeliveryAddressDto = z.object({ id: uuid, name: z.string(), company: z.string().nullable(), department: z.string().nullable(), street1: z.string(), zip: z.string(), city: z.string(), countryCode: z.string() });
export const sfParcelShopDto = z.object({ number: z.string(), name: z.string(), street: z.string(), zip: z.string(), city: z.string(), countryCode: z.string(), distanceM: z.number().nullable() });
//# sourceMappingURL=checkout.js.map